import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import {
  Typography,
  Link,
  Grid,
  Box,
  LinearProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  FormButton,
  FormSelect,
  FormCheckbox,
  FormDatePicker,
} from "components";
import { Alert } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { addDays, format as DateFormat } from "date-fns";
import {
  fetchOrganizationsForEnvironmentRequested,
  selectEnvironmentOrganizations,
  selectEnvironmentOrganizationsStatus,
  selectEnvironmentOrganizationsError,
  resetData as refreshOrganizationList,
} from "stores/organizations/organizationsSlice";
import {
  fetchBranchesRequested,
  selectBranches,
  selectRepoStatuses,
  selectRepoErrors,
  resetData as refreshBranchList,
} from "stores/branches/branchesSlice";
import {
  fetchOndemandRequested,
  selectOndemandSite,
  createOndemandRequested,
  selectIsFinishedCreating as selectIsOndemandFinishedCreating,
  selectIsSaving as selectOndemandSaving,
  selectSucceeded as selectOndemandSucceeded,
  selectError as selectOndemandError,
  resetSaveState,
} from "stores/ondemand/ondemandSlice";

import {
  ONDEMAND_SOURCE_ENVIRONMENT,
  PRODUCT_CONFIG,
  organizationEnvironmentFor,
  buildOndemandLink,
} from "configs/products";
import { DATE_TIME_FORMATS } from "common/constants";
import useUser from "hooks/useUser";

const productOption = (productKey) => {
  const config = PRODUCT_CONFIG[productKey];
  if (!config) {
    return null;
  }
  return { value: productKey, label: config.label };
};

const CreateEnvironment = ({
  embedded = false,
  defaultProduct = null,
  onCreateCompleted,
}) => {
  const dispatch = useDispatch();
  const user = useUser();
  const isFinishedCreating = useSelector(selectIsOndemandFinishedCreating);
  const isSaving = useSelector(selectOndemandSaving);
  const succeeded = useSelector(selectOndemandSucceeded);
  const error = useSelector(selectOndemandError);
  const environmentOrganizations = useSelector(selectEnvironmentOrganizations);
  const branches = useSelector(selectBranches);
  const repoStatus = useSelector(selectRepoStatuses);
  const repoErrors = useSelector(selectRepoErrors);
  const ondemandSite = useSelector(selectOndemandSite);
  const maxDate = addDays(new Date(), 21);
  const completionNotified = React.useRef(false);

  const products = Object.fromEntries(
    Object.entries(PRODUCT_CONFIG).map(([value, config]) => [
      value,
      config.label,
    ])
  );

  // formValues.product is the product key string (FormSelect onChange passes
  // option.value). RHF / Autocomplete hold the full { value, label } option.
  const initialFormState = {
    product: defaultProduct || null,
    fromEnvironment: null,
    targetOrgEnvironment: null,
    organization: null,
    beBranch: null,
    beCustomBranch: null,
    feBranch: null,
    feCustomBranch: null,
    deidentify: true,
    expiryDate: addDays(new Date(), 3),
  };

  const [formValues, setFormValues] = React.useState(initialFormState);
  const selectedProductConfig = PRODUCT_CONFIG[formValues.product];
  const selectedProductRepos = selectedProductConfig
    ? Object.values(selectedProductConfig.repos)
    : [];
  const productEnvironments = selectedProductConfig?.environments ?? [];

  // Most products pick the environment to copy data FROM. EnrollPro always copies
  // from production, so its dropdown picks the environment the site runs AS.
  const picksTargetEnvironment = Boolean(
    selectedProductConfig?.usesTargetOrgEnvironment
  );
  const environmentField = picksTargetEnvironment
    ? "targetOrgEnvironment"
    : "fromEnvironment";
  const selectedEnvironment = formValues[environmentField];

  // EnrollPro lists organizations from production regardless of the target env.
  const organizationEnvironment = organizationEnvironmentFor(
    formValues.product,
    selectedEnvironment
  );

  const organizationStatus = useSelector((state) =>
    selectEnvironmentOrganizationsStatus(
      state,
      formValues.product,
      organizationEnvironment
    )
  );
  const organizationLoadError = useSelector((state) =>
    selectEnvironmentOrganizationsError(
      state,
      formValues.product,
      organizationEnvironment
    )
  );

  // Load branches only for the selected product.
  React.useEffect(() => {
    if (!selectedProductConfig) {
      return;
    }

    selectedProductRepos.forEach((repo) => {
      const status = repoStatus[repo];
      // 'failed' is sticky until Retry / Refresh — do not auto-loop.
      if (status === "loading" || status === "succeeded" || status === "failed") {
        return;
      }
      if (branches.hasOwnProperty(repo)) {
        return;
      }
      dispatch(fetchBranchesRequested({ repo, product: formValues.product }));
    });
    // eslint-disable-next-line
  }, [dispatch, formValues.product, repoStatus, branches]);

  // Load orgs once we know which environment to list them from.
  React.useEffect(() => {
    const product = formValues.product;
    const environment = organizationEnvironment;
    if (!product || !environment) {
      return;
    }

    // 'failed' is sticky until Retry / Refresh — do not auto-loop.
    if (
      organizationStatus === "loading" ||
      organizationStatus === "succeeded" ||
      organizationStatus === "failed"
    ) {
      return;
    }

    if (environmentOrganizations[product]?.hasOwnProperty(environment)) {
      return;
    }

    dispatch(
      fetchOrganizationsForEnvironmentRequested({
        environment,
        product,
      })
    );
    // eslint-disable-next-line
  }, [
    dispatch,
    formValues.product,
    organizationEnvironment,
    organizationStatus,
    environmentOrganizations,
  ]);

  React.useEffect(() => {
    if (succeeded && !isFinishedCreating) {
      setTimeout(() => dispatch(fetchOndemandRequested()), 5000);
    }
  }, [dispatch, succeeded, isFinishedCreating]);

  React.useEffect(() => {
    if (
      succeeded &&
      isFinishedCreating &&
      ondemandSite?.SK &&
      onCreateCompleted &&
      !completionNotified.current
    ) {
      completionNotified.current = true;
      onCreateCompleted(ondemandSite);
    }
  }, [succeeded, isFinishedCreating, ondemandSite, onCreateCompleted]);

  React.useEffect(() => {
    return () => {
      dispatch(resetSaveState());
    };
  }, [dispatch]);

  const buildSchema = () =>
    yup.object().shape({
      product: yup.object().typeError("Required").required("Required"),
      fromEnvironment: yup
        .object()
        .typeError("Required")
        .when("product", (product, schema) => {
          if (PRODUCT_CONFIG[product?.value]?.usesTargetOrgEnvironment) {
            return schema.nullable();
          }
          return schema.required("Required");
        }),
      targetOrgEnvironment: yup
        .object()
        .typeError("Required")
        .when("product", (product, schema) => {
          if (PRODUCT_CONFIG[product?.value]?.usesTargetOrgEnvironment) {
            return schema.required("Required");
          }
          return schema.nullable();
        }),
      organization: yup
        .object()
        .shape({
          id: yup.number(),
          subdomain: yup.string(),
        })
        .typeError("Required")
        .required("Required"),
      beBranch: yup.object().typeError("Required").required("Required"),
      beCustomBranch: yup
        .object()
        .typeError("Required")
        .when("product", (product, schema)=>{
          if(PRODUCT_CONFIG[product?.value]?.hasCustomBranches) {
            return schema.required("Required");
          }
          return schema.nullable();
        }),
      feBranch: yup
        .object()
        .typeError("Required")
        .when("product", (product, schema)=>{
          if(PRODUCT_CONFIG[product?.value]?.hasFrontend) {
            return schema.required("Required");
          }
          return schema.nullable();
        }),
      feCustomBranch: yup
        .object()
        .typeError("Required")
        .when("product", (product, schema)=>{
          if(PRODUCT_CONFIG[product?.value]?.hasCustomBranches) {
            return schema.required("Required");
          }
          return schema.nullable();
        }),
      deidentify: yup.boolean().typeError("Required"),
      expiryDate: yup
        .date()
        .min(new Date(), "Expiration cannot be in the past.")
        .max(maxDate, "Expiration cannot be more than 21 days in the future.")
        .typeError("Required")
        .required("Required"),
    });

  const buildOptions = (optionsObj) => {
    const options = Object.entries(optionsObj).map(([value, label]) => {
      return {
        value,
        label,
      };
    });

    return options;
  };

  const buildEnvironmentOptions = () => {
    const options = productEnvironments.map((env) => ({
      value: env,
      label: env.charAt(0).toUpperCase() + env.slice(1),
    }));

    return options.sort((a, b) => -b.label.localeCompare(a.label));
  };

  const selectedOrganizations = () =>
    environmentOrganizations[formValues.product]?.[organizationEnvironment];

  const isOrganizationLoading = organizationStatus === "loading";

  const buildOrganizationOptions = () => {
    const organizations = selectedOrganizations();
    if (!selectedProductConfig || organizations === undefined) {
      return [];
    }

    return organizations.map((org) => ({
      value: org.id,
      label: selectedProductConfig.organizationLabel(org),
      subdomain: org.subdomain,
      organization_name: org.organization_name,
    }));
  };

  const isBranchLoading = (repo) => repoStatus[repo] === "loading";

  const branchLoadError = (repo) => repoErrors[repo] ?? null;

  const buildBranchOptions = (repo) => {
    if (branches[repo] === undefined) {
      return [];
    }

    return branches[repo].map((item) => ({ value: item, label: item }));
  };

  const retryOrganizations = () => {
    if (!formValues.product || !organizationEnvironment) {
      return;
    }
    dispatch(
      fetchOrganizationsForEnvironmentRequested({
        product: formValues.product,
        environment: organizationEnvironment,
      })
    );
  };

  const retryBranches = (repo) => () => {
    if (!formValues.product || !repo) {
      return;
    }
    dispatch(
      fetchBranchesRequested({ repo, product: formValues.product })
    );
  };

  const siteLink = buildOndemandLink(ondemandSite);

  const isProductSelected = Boolean(selectedProductConfig);

  const {
    handleSubmit,
    control,
    resetField,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...initialFormState,
      product: productOption(defaultProduct),
    },
    resolver: yupResolver(buildSchema()),
  });

  const handleFieldChange = (input, newValue) => {
    const updatedformValues = {
      ...formValues,
      [input]: newValue,
    };
    setFormValues(updatedformValues);
  };

  const handleCreateOndemand = (formData) => {
    const productConfig = PRODUCT_CONFIG[formData["product"]?.value];
    let payload = {
      email: user.email
    };
    Object.keys(formData).forEach((inputKey) => {
      if (inputKey === "expiryDate") {
        payload[inputKey] = DateFormat(
          formData[inputKey],
          DATE_TIME_FORMATS.outputDate
        );
      } else if (inputKey === "deidentify") {
        const product = PRODUCT_CONFIG[formData["product"]?.value];
        payload[inputKey] = product?.hasDeidentify
          ? Number(formData[inputKey])
          : 1;
      } else if (inputKey === "organization") {
        payload["orgId"] = formData[inputKey].value;
        payload["orgSubdomain"] = formData[inputKey].subdomain;
      } else if (formData.hasOwnProperty(inputKey) && null !== formData[inputKey]) {
        payload[inputKey] = formData[inputKey].value;
      }
    });

    // The dropdown chose the target environment, so the source is fixed.
    if (productConfig?.usesTargetOrgEnvironment) {
      payload["fromEnvironment"] = ONDEMAND_SOURCE_ENVIRONMENT;
    }

    dispatch(createOndemandRequested(payload));
  };

  const handleProductChange = (_, newValue) => {
    resetField("fromEnvironment", {keepError:true});
    resetField("targetOrgEnvironment", {keepError:true});
    resetField("organization", {keepError:true});
    resetField("beBranch", {keepError:true});
    resetField("beCustomBranch", {keepError:true});
    resetField("feBranch", {keepError:true});
    resetField("feCustomBranch", {keepError:true});
    resetField("deidentify", {defaultValue: true});
    setFormValues((current) => ({
      ...current,
      product: newValue,
      fromEnvironment: null,
      targetOrgEnvironment: null,
      organization: null,
    }));
  };

  const handleOrganizationChange = (selectedValue) => {
    handleFieldChange("organization", {
      id: selectedValue.value,
      subdomain: selectedValue.subdomain,
    });
  };

  const handleAutocompleteOnChange = (event, selectedValue, field) =>
    selectedValue
      ? handleFieldChange(field, String(selectedValue.value))
      : handleFieldChange(field, "");

  const handleRefreshData = () => {
    dispatch(refreshBranchList());
    dispatch(refreshOrganizationList());
  };

  const getErrorBanner = () => {
    if (error) {
      return (
        <Alert severity="error">
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      );
    }
  };

  const getSuccessBanner = () => {
    if (isFinishedCreating && ondemandSite.SK) {
      return (
        <Alert severity="success">
          Ondemand Site Created Successfully, visit:{" "}
          <Link target="_blank" href={siteLink}>
            {siteLink}
          </Link>
        </Alert>
      );
    }
  };

  const getProgressBanner = () => {
    if (!error && !isFinishedCreating) {
      return (
        <>
          <Alert severity="info">Ondemand site is being created.</Alert>
          <LinearProgress />
        </>
      );
    }
  };

  const renderForm = () => (
    <>
      {isProductSelected ? (
        <>
          <Grid item>
            <FormSelect
              options={buildEnvironmentOptions()}
              name={environmentField}
              label={
                picksTargetEnvironment
                  ? "Target Environment"
                  : "Source Environment"
              }
              errors={errors}
              isDisabled={isSaving || !isFinishedCreating}
              onChange={handleFieldChange}
              control={control}
              description={
                picksTargetEnvironment
                  ? "Environment the new Ondemand site runs as. Data is always copied from production."
                  : "Environment to extract data from for the new Ondemand site."
              }
            />
          </Grid>

          <Grid item>
            <FormSelect
              options={buildOrganizationOptions()}
              name="organization"
              label="Organization"
              errors={errors}
              isDisabled={isSaving || !isFinishedCreating}
              isLoading={isOrganizationLoading}
              loadError={organizationLoadError}
              onRetry={retryOrganizations}
              onChange={handleOrganizationChange}
              control={control}
              description={`Organization to extract from ${organizationEnvironment ?? ""}`.trim()}
            />
          </Grid>

          <Grid item>
            <FormSelect
              options={buildBranchOptions(selectedProductConfig.repos.be)}
              name="beBranch"
              label="Back End Branch"
              errors={errors}
              isDisabled={isSaving || !isFinishedCreating}
              isLoading={isBranchLoading(selectedProductConfig.repos.be)}
              loadError={branchLoadError(selectedProductConfig.repos.be)}
              onRetry={retryBranches(selectedProductConfig.repos.be)}
              onChange={handleAutocompleteOnChange}
              control={control}
              description="Back end branch to checkout for on demand environment"
            />
          </Grid>

          {selectedProductConfig.hasCustomBranches ? (
            <Grid item>
              <FormSelect
                options={buildBranchOptions(
                  selectedProductConfig.repos.beCustom
                )}
                name="beCustomBranch"
                label="Custom Back End Branch"
                errors={errors}
                isDisabled={isSaving || !isFinishedCreating}
                isLoading={isBranchLoading(selectedProductConfig.repos.beCustom)}
                loadError={branchLoadError(selectedProductConfig.repos.beCustom)}
                onRetry={retryBranches(selectedProductConfig.repos.beCustom)}
                onChange={handleAutocompleteOnChange}
                control={control}
                description="Custom Back end branch to checkout for on demand environment"
              />
            </Grid>
          ) : (
            <></>
          )}

          {selectedProductConfig.hasFrontend ? (
            <Grid item>
              <FormSelect
                options={buildBranchOptions(selectedProductConfig.repos.fe)}
                name="feBranch"
                label="Front End Branch"
                errors={errors}
                isDisabled={isSaving || !isFinishedCreating}
                isLoading={isBranchLoading(selectedProductConfig.repos.fe)}
                loadError={branchLoadError(selectedProductConfig.repos.fe)}
                onRetry={retryBranches(selectedProductConfig.repos.fe)}
                onChange={handleAutocompleteOnChange}
                control={control}
                description="Front end branch to checkout for on demand environment"
              />
            </Grid>
          ) : (
            <></>
          )}
          {selectedProductConfig.hasCustomBranches ? (
            <Grid item>
              <FormSelect
                options={buildBranchOptions(
                  selectedProductConfig.repos.feCustom
                )}
                name="feCustomBranch"
                label="Custom Front End Branch"
                errors={errors}
                isDisabled={isSaving || !isFinishedCreating}
                isLoading={isBranchLoading(selectedProductConfig.repos.feCustom)}
                loadError={branchLoadError(selectedProductConfig.repos.feCustom)}
                onRetry={retryBranches(selectedProductConfig.repos.feCustom)}
                onChange={handleAutocompleteOnChange}
                control={control}
                description="Custom Front end branch to checkout for on demand environment"
              />
            </Grid>
          ) : (
            <></>
          )}

          <Grid item>
            <FormDatePicker
              name="expiryDate"
              label="Expiration Date"
              value={formValues.expiryDate}
              onChange={handleFieldChange}
              control={control}
              errors={errors}
              disablePast={true}
              maxDate={maxDate}
              isDisabled={isSaving || !isFinishedCreating}
              defaultValue={formValues.expiryDate}
              description="The last day this environment will be available"
            />
          </Grid>

          {selectedProductConfig.hasDeidentify ? (
            <Grid item>
              <FormCheckbox
                name="deidentify"
                label="De-identify data?"
                defaultChecked={formValues.deidentify}
                onChange={handleFieldChange}
                control={control}
                errors={errors}
                isDisabled={isSaving || !isFinishedCreating}
              />
            </Grid>
          ) : (
            <></>
          )}

          <Grid item>
            <Box>
              <FormButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                value={isFinishedCreating ? "Create" : "Creating..."}
                isSubmitting={isSaving || !isFinishedCreating}
                disableElevation
              />
            </Box>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );

  const formContent = (
    <form onSubmit={handleSubmit(handleCreateOndemand)}>
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <FormSelect
            options={buildOptions(products)}
            name="product"
            label="Select Product"
            errors={errors}
            isDisabled={isSaving || !isFinishedCreating}
            onChange={handleProductChange}
            control={control}
            description="Product to create a new Ondemand site for"
          />
        </Grid>

        {renderForm()}
      </Grid>
    </form>
  );

  if (embedded) {
    return (
      <Box width="100%">
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <Tooltip title="Refresh Lists">
            <IconButton
              color="primary"
              onClick={handleRefreshData}
              size="large"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        {getErrorBanner()}
        {getSuccessBanner()}
        {getProgressBanner()}
        <Box mt={2}>{formContent}</Box>
      </Box>
    );
  }

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center">
      <Grid container item justifyContent="space-between" xs={6}>
        <Grid item>
          <Box width="inherit" mt={3}>
            <Typography variant="h4" align="center" color="primary">
              Create New On-Demand Site
            </Typography>
          </Box>
        </Grid>
        <Grid item>
          <Box width="inherit" mt={2}>
            <Tooltip title="Refresh Lists">
              <IconButton
                color="primary"
                onClick={handleRefreshData}
                size="large"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Grid container alignItems="center" justifyContent="center">
        <Grid item lg={6} sm={12}>
          {getErrorBanner()}
          {getSuccessBanner()}
          {getProgressBanner()}
        </Grid>
      </Grid>
      <Grid container justifyContent="center">
        <Grid item xs={6}>
          <Box width="inherit" mt={2}>
            {formContent}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CreateEnvironment;
